import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import SidebarLayout from 'components/Layout/Layout';
import { API_URL } from 'env';
import { Modal, Select, Collapse, Card, Row, Col, Input, Button, Switch } from 'antd';
import TinyMce from 'components/GbCpn/TinyMce';
import ImageUpload from 'components/GbCpn/ImageUpload';
import { FileAddOutlined, DeleteOutlined, EditOutlined, DragOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './AddPoint.scss';

interface DescriptionItem {
  id: string;
  title: string;
  content: string;
  image: string;
  position: 'Left' | 'Right' | 'Bottom' | 'Top';
}

interface Product {
  id: number;
  title: string;
  subtitle: string;
  point_price: number;
  locale: string;
  image: string;
  description: string;
}

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const [bonuses, setBonuses] = useState<Product[]>([]);
  const [bonusLoading, setBonusLoading] = useState(false);

  const [form, setForm] = useState({
    code: '',
    name: '',
    points: null,
    max_times_per_user: null,
    locale: 'vi',
    description: '',
    is_active: true,
    task_type: ''
  } as any);

  const [descriptions, setDescriptions] = useState([] as any);
  const [loading, setLoading] = useState(false);

  const fetchBonuses = async () => {
    try {
      setBonusLoading(true);
      const response = await fetch(`${API_URL}api/v1/be/point_rules`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const json = await response.json();

      if (json.status && json.data && json.data.items) {
        const productData = Array.isArray(json.data.items) ? json.data.items : [];
        setBonuses(productData);
        console.log('Products data loaded:', productData);
      } else {
        console.error('Error fetching bonuses:', json.message);
        Modal.error({
          title: 'Lỗi',
          content: json.message || 'Không thể tải danh sách products',
        });
      }
    } catch (error: any) {
      console.error('Error fetching bonuses:', error);
      setBonuses([]);
      Modal.error({
        title: 'Lỗi kết nối',
        content: 'Không thể kết nối đến server để lấy danh sách products',
      });
    } finally {
      setBonusLoading(false);
    }
  };

  useEffect(() => {
    fetchBonuses();
  }, []);

  const handleSave = async () => {
    if (!form.code.trim()) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập code' });
      return;
    }
    if (!form.name.trim()) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập tên' });
      return;
    }
    if (form.points <= 0) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập point' });
      return;
    }
    if (form.max_times_per_user <= 0) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập Max Times per User' });
      return;
    }
    if (!form.description.trim()) {
      Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập mô tả' });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...form,
      };

      const response = await fetch(`${API_URL}api/v1/be/point_rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const json = await response.json();

      if (json.status) {
        Modal.success({
          title: 'Success',
          content: 'Operation completed successfully',
          onOk: () => navigate('/point'),
        });
      } else {
        Modal.error({
          title: 'Error',
          content: json.error[Object.keys(json.error)[0]] || 'Unknown error',
        });
      }
    } catch (error: any) {
      Modal.error({
        title: 'Error',
        content: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SidebarLayout title={<h4 className="text-2xl font-bold !m-0 py-4"></h4>}>
      <div className='head-form-box mb-4'>
        <div className='box-left'>
          <ArrowLeftOutlined
            onClick={() => navigate('/point')}
          />
          <div className='title'>Add Point</div>
        </div>
        <Button loading={loading} onClick={handleSave} type="primary" icon={<SaveOutlined />} size="large">
          Add new
        </Button>
      </div>
      <Row gutter={[16, 16]} className='AddProduct'>
        <Col span={24} lg={18}>
          <Card>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Code
                  </label>
                  <Input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Name
                  </label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Points
                  </label>
                  <Input
                    type="number"
                    value={form.points}
                    onChange={(e) => setForm({ ...form, points: e.target.value })}
                  />
                </div>
              </Col>

              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Description
                  </label>
                  <Input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Max Times Per User
                  </label>
                  <Input
                    type="number"
                    value={form.max_times_per_user}
                    onChange={(e) => setForm({ ...form, max_times_per_user: e.target.value })}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Task Type
                  </label>
                  <Input
                    type="text"
                    value={form.task_type}
                    onChange={(e) => setForm({ ...form, task_type: e.target.value })}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={24} lg={6}>
          <Card>
            {/* <div className="mb-4">
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Locale
              </label>
              <Select
                onChange={(e) => setForm({ ...form, locale: e })}
                value={form.locale}
                style={{ width: '100%' }}
              >
                <option value="vi">Vietnam</option>
                <option value="en">English</option>
              </Select>
            </div> */}
            <div>
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Active
              </label>
              <Switch checked={form.is_active} onChange={(value) => setForm({ ...form, is_active: value })} />
            </div>
          </Card>
        </Col>
      </Row>
      <div className='ctrbox-foot mt-4'>
        <Button loading={loading} onClick={handleSave} type="primary" icon={<SaveOutlined />} size="large">
          Add new
        </Button>
      </div>
    </SidebarLayout>
  );
};

export default AddProduct;